import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BookingAssessmentForm } from '../providers/booking-assessment.provider';

export const useBookingValidation = (values: BookingAssessmentForm) => {
  const { t } = useTranslation();
  const validationState = useMemo(() => {
    const hasAssessment = !!(values.assessment_id && values.assessment);
    
    const hasSelectedPatient = !!(values.patient && values.patient_id);
    const hasCompleteGuestData = !!(
      values.patient?.name && 
      values.patient?.gender &&
      values.patient?.date_of_birth &&
      values.patient?.email && 
      values.patient?.phone_number
    );
    const hasPatient = hasSelectedPatient || hasCompleteGuestData;
    
    const requiresSchedule = values.assessment?.has_schedule;
    const hasPsychologistSelected = !!(values.psychologist && values.psychologist_id);
    
    let hasValidSchedule = true;
    let incompleteSessionIndexes: number[] = [];
    
    if (requiresSchedule) {
      const hasPsychologist = !!(values.psychologist && values.psychologist_id);
      const hasSchedules = !!(values.schedule_ids && values.schedule_ids.length > 0);
      const hasTimes = !!(values.time_ids && values.time_ids.length > 0);

      const scheduleTimesMatch = values.schedule_ids?.length === values.time_ids?.length;

      hasValidSchedule = hasPsychologist && hasSchedules && hasTimes && scheduleTimesMatch;
      
      // Check for incomplete sessions
      if (hasSchedules && values.schedule_ids && values.time_ids) {
        incompleteSessionIndexes = values.schedule_ids
          .map((scheduleId, index) => {
            const hasSchedule = scheduleId !== undefined && scheduleId !== null;
            const hasTime = values.time_ids && values.time_ids[index] !== undefined && values.time_ids[index] !== null;
            return hasSchedule && !hasTime ? index : -1;
          })
          .filter(index => index !== -1);
      }
    }

    const continueAsGuest = values?.continueAsGuest === true;
    const patientDataIncomplete = 
      values?.patient?.name ||
      values?.patient?.gender ||
      values?.patient?.date_of_birth ||
      values?.patient?.email ||
      values?.patient?.phone_number;

    return {
      step0Valid: hasAssessment && (!requiresSchedule || hasPsychologistSelected),
      step1Valid: hasAssessment && hasPatient && hasValidSchedule && incompleteSessionIndexes.length === 0,
      step2Valid: hasAssessment && hasPatient && hasValidSchedule && incompleteSessionIndexes.length === 0,
      hasAssessment,
      hasPatient,
      requiresSchedule,
      hasValidSchedule,
      incompleteSessionIndexes,
      continueAsGuest,
      patientDataIncomplete,
    };
  }, [values]);

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return validationState.step0Valid;
      case 1:
        return validationState.step1Valid;
      case 2:
        return validationState.step2Valid;
      default:
        return false;
    }
  };

  const getValidationError = (step: number): string | null => {
    const { 
      hasAssessment, 
      requiresSchedule, 
      hasValidSchedule, 
      hasPatient, 
      incompleteSessionIndexes,  
      continueAsGuest,
      patientDataIncomplete,
    } = validationState;
    
    switch (step) {
      case 0:
        if (!hasAssessment) return t('notification.error.selectItem', { item: t('common.text.package') });
        if (requiresSchedule && !values.psychologist)
          return t('notification.error.selectItem', { item: t('common.text.psychologist') });
        break;
      case 1:
        if (!hasAssessment) return t('notification.error.selectItem', { item: t('common.text.package') });

        if (requiresSchedule && !hasValidSchedule) {
          if (!values.psychologist) return t('notification.error.selectItem', { item: t('common.text.psychologist') });
          if (!values.schedule_ids?.length) return t('notification.error.selectItem', { item: t('common.text.date') });
          if (!values.time_ids?.length) return t('notification.error.selectItem', { item: t('common.text.time') });
          if (values.schedule_ids?.length !== values.time_ids?.length) {
            return t('notification.error.pleaseCompleteTheForm');
          }
        }

        if (continueAsGuest && patientDataIncomplete) {
          return t('notification.error.pleaseCompleteTheForm');
        }

        if (!hasPatient && incompleteSessionIndexes.length === 0) {
          if (requiresSchedule) {
            return t('notification.error.pleaseCompleteSchedule');
          }
          return t('notification.error.selectItem', { item: t('common.text.patient') });
        }

        break;
      case 2:
        return getValidationError(1);
    }
    
    return null;
  };

  const getIncompleteSessionIndexes = (): number[] => {
    return validationState.incompleteSessionIndexes;
  };

  return {
    isStepValid,
    getValidationError,
    validationState,
    getIncompleteSessionIndexes,
  };
};